/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { LoaderCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

/* Import components */
import CodeBlock from '@/components/code/CodeBlock';

/* Import configuration */
import { mergeSystemConfigurationDefaults } from '@/config/systemConfigurationSchema';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';
import { useSession } from '@/context/SessionContext';

/* Import libraries */
import { EeCheckApiError, eeCheckApi } from '@/api/eeCheckApi';
import { isExpiredSessionError } from '@/lib/app/debugRequests';
import {
    getConfigurationCustomers,
    getConfigurationSystems,
    type ConfigurationCustomer,
} from '@/lib/configuration/configurationSystems';

interface ConfigurationTarget {
    customer: ConfigurationCustomer;
    system: string;
}

interface ConfigurationEntry extends ConfigurationTarget {
    content: string | null;
    error: string | null;
    status: 'data' | 'empty' | 'error' | 'loading';
}

interface AllConfigurationsTabProps {
    theme: 'dark' | 'light';
}

function createConfigurationTargets(): ConfigurationTarget[] {
    return getConfigurationCustomers().flatMap((customer) => (
        getConfigurationSystems(customer).map((system) => ({ customer, system }))
    ));
}

function createLoadingEntries(targets: ConfigurationTarget[]): ConfigurationEntry[] {
    return targets.map((target) => ({
        ...target,
        content: null,
        error: null,
        status: 'loading',
    }));
}

function AllConfigurationsTab({ theme }: AllConfigurationsTabProps) {
    const { t } = useI18n();
    const { accessToken, isSessionExpired, onSessionExpired } = useSession();
    const targets = useMemo(createConfigurationTargets, []);
    const [entries, setEntries] = useState<ConfigurationEntry[]>(() => createLoadingEntries(targets));
    const [completedCount, setCompletedCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!accessToken || isSessionExpired) {
            return;
        }

        let isCancelled = false;

        async function loadConfigurations() {
            setEntries(createLoadingEntries(targets));
            setCompletedCount(0);
            setIsLoading(true);

            for (const target of targets) {
                try {
                    const { data } = await eeCheckApi.readConfiguration(accessToken, target);
                    let entry: ConfigurationEntry;

                    if (data.success && data.data !== null) {
                        entry = {
                            ...target,
                            content: JSON.stringify(
                                mergeSystemConfigurationDefaults(data.data.data, target.system),
                                null,
                                4,
                            ),
                            error: null,
                            status: 'data',
                        };
                    } else {
                        entry = {
                            ...target,
                            content: null,
                            error: null,
                            status: 'empty',
                        };
                    }

                    if (isCancelled) {
                        return;
                    }

                    setEntries((currentEntries) => currentEntries.map((currentEntry) => (
                        currentEntry.customer === target.customer && currentEntry.system === target.system
                            ? entry
                            : currentEntry
                    )));
                    setCompletedCount((currentCount) => currentCount + 1);
                } catch (error) {
                    if (isExpiredSessionError(error)) {
                        onSessionExpired();
                        return;
                    }

                    if (isCancelled) {
                        return;
                    }

                    const message = error instanceof EeCheckApiError || error instanceof Error
                        ? error.message
                        : t('Unknown error.');

                    setEntries((currentEntries) => currentEntries.map((currentEntry) => (
                        currentEntry.customer === target.customer && currentEntry.system === target.system
                            ? { ...currentEntry, error: message, status: 'error' }
                            : currentEntry
                    )));
                    setCompletedCount((currentCount) => currentCount + 1);
                }
            }

            if (!isCancelled) {
                setIsLoading(false);
            }
        }

        void loadConfigurations();

        return () => {
            isCancelled = true;
        };
    }, [accessToken, isSessionExpired, onSessionExpired, t, targets]);

    return (
        <div className="flex w-full flex-col gap-6">
            <section className="flex flex-col gap-2">
                <h2>{t('All configurations')}</h2>
                <p>{t('Read-only overview of all available customer and system configurations.')}</p>
            </section>

            {isLoading ? (
                <section className="dashboard-panel flex items-center gap-3 p-5" aria-live="polite">
                    <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
                    <span>
                        {t('Configuration data is loading {completed}/{total}.', {
                            completed: completedCount,
                            total: targets.length,
                        })}
                    </span>
                </section>
            ) : null}

            {entries.map((entry) => (
                <section key={`${entry.customer}-${entry.system}`} className="dashboard-panel flex flex-col gap-4 p-5">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <h3>{entry.system}</h3>
                        <span className="text-sm text-base-content/70">{entry.customer === 'muenster' ? t('Münster') : t('Neutraubling')}</span>
                    </div>

                    {entry.status === 'loading' ? (
                        <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                            {t('Configuration data is loading.')}
                        </div>
                    ) : null}
                    {entry.status === 'empty' ? <p>{t('No data available.')}</p> : null}
                    {entry.status === 'error' ? <p className="text-error">{entry.error}</p> : null}
                    {entry.status === 'data' && entry.content ? (
                        <CodeBlock code={entry.content} language="json" theme={theme} wrapperClassName="w-full" />
                    ) : null}
                </section>
            ))}
        </div>
    );
}

export default AllConfigurationsTab;
